import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

const GAFFIT_REELS_FALLBACK_STYLE_ID = 'gaffit-reels-compact-fallback-style';

function ensureGaffitReelsFallbackStyle() {
  if (document.getElementById(GAFFIT_REELS_FALLBACK_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = GAFFIT_REELS_FALLBACK_STYLE_ID;
  style.textContent = `
    .dwell-shoppable-video-section .dwell-sv-modal {
      padding: 10px !important;
      align-items: center !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-shell {
      width: min(96vw, calc(92vh * 16 / 9), 1240px) !important;
      height: auto !important;
      max-height: 92vh !important;
      aspect-ratio: 16 / 9 !important;
      border-radius: 6px !important;
      overflow: hidden !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-content,
    .dwell-shoppable-video-section .dwell-sv-modal-panel {
      height: 100% !important;
      min-height: 0 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-panel {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(360px, 0.95fr) !important;
      overflow: hidden !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-video {
      min-width: 0 !important;
      min-height: 0 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-video video,
    .dwell-shoppable-video-section .dwell-sv-modal-video iframe,
    .dwell-shoppable-video-section .dwell-sv-modal-video img {
      object-fit: contain !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-product {
      display: grid !important;
      grid-template-rows: auto auto auto minmax(0, 1fr) auto !important;
      height: 100% !important;
      min-height: 0 !important;
      overflow: hidden !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-product-media {
      padding: 14px 22px 6px !important;
      min-height: 0 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-main-image {
      width: min(30vh, 180px) !important;
      height: min(30vh, 180px) !important;
      object-fit: contain !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-product-summary {
      padding: 0 22px 8px !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-product-title {
      font-size: clamp(18px, 2.2vh, 26px) !important;
      line-height: 1.1 !important;
      -webkit-line-clamp: 2 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-price-row {
      margin-top: 6px !important;
    }

    .dwell-shoppable-video-section .dwell-sv-price {
      font-size: clamp(18px, 2.2vh, 26px) !important;
      line-height: 1 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-compare {
      font-size: clamp(12px, 1.5vh, 15px) !important;
    }

    .dwell-shoppable-video-section .dwell-sv-option-wrap {
      padding: 8px 22px !important;
      border-top: 1px solid #eeeeee !important;
      border-bottom: 1px solid #eeeeee !important;
    }

    .dwell-shoppable-video-section .dwell-sv-option-label {
      margin: 0 0 8px !important;
      font-size: clamp(12px, 1.55vh, 16px) !important;
      line-height: 1.15 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-option-list {
      gap: 8px !important;
    }

    .dwell-shoppable-video-section .dwell-sv-option-pill {
      max-width: 260px !important;
      min-height: 36px !important;
      padding: 8px 12px !important;
      border-radius: 6px !important;
      font-size: clamp(12px, 1.55vh, 15px) !important;
      line-height: 1.1 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .dwell-shoppable-video-section .dwell-sv-description-panel {
      padding: 12px 22px !important;
      overflow: auto !important;
      border-bottom: 1px solid #eeeeee !important;
    }

    .dwell-shoppable-video-section .dwell-sv-description-panel h3 {
      margin: 0 0 6px !important;
      font-size: clamp(14px, 1.8vh, 17px) !important;
      line-height: 1.1 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-description-panel p {
      font-size: clamp(12px, 1.55vh, 14px) !important;
      line-height: 1.35 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-shop-form {
      margin: 0 !important;
      padding: 12px 22px !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-actions {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
      gap: 10px !important;
      align-items: center !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-add {
      order: 1 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-buy-now {
      order: 2 !important;
      border-color: #111111 !important;
      background: #111111 !important;
      color: #ffffff !important;
    }

    .dwell-shoppable-video-section .dwell-sv-more-info {
      order: 3 !important;
      grid-column: 1 / -1 !important;
      min-height: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      color: #111111 !important;
      font-size: 13px !important;
      line-height: 1.2 !important;
      font-weight: 700 !important;
      justify-self: start !important;
      text-decoration: underline !important;
    }

    .dwell-shoppable-video-section .dwell-sv-modal-action {
      min-height: 42px !important;
      padding: 10px 12px !important;
      border-radius: 6px !important;
      font-size: 14px !important;
      line-height: 1.1 !important;
    }

    .dwell-shoppable-video-section .dwell-sv-cart-link {
      display: none !important;
    }

    @media screen and (max-width: 749px) {
      .dwell-shoppable-video-section .dwell-sv-modal {
        padding: 8px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-shell {
        width: min(96vw, calc(92vh * 16 / 9)) !important;
        aspect-ratio: 16 / 9 !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-panel {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-close {
        top: 8px !important;
        right: 8px !important;
        width: 34px !important;
        height: 34px !important;
        font-size: 24px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-nav {
        width: 30px !important;
        height: 38px !important;
        font-size: 24px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-product-media {
        padding: 8px 10px 2px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-main-image {
        width: min(15vh, 82px) !important;
        height: min(15vh, 82px) !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-product-summary {
        padding: 0 10px 4px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-product-title {
        font-size: clamp(12px, 1.8vh, 15px) !important;
      }

      .dwell-shoppable-video-section .dwell-sv-price {
        font-size: clamp(13px, 1.9vh, 16px) !important;
      }

      .dwell-shoppable-video-section .dwell-sv-option-wrap,
      .dwell-shoppable-video-section .dwell-sv-description-panel,
      .dwell-shoppable-video-section .dwell-sv-shop-form {
        padding: 5px 10px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-option-label,
      .dwell-shoppable-video-section .dwell-sv-description-panel h3 {
        margin-bottom: 4px !important;
        font-size: 11px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-option-pill {
        min-height: 26px !important;
        max-width: 150px !important;
        padding: 5px 8px !important;
        font-size: 10px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-description-panel p {
        font-size: 10px !important;
        line-height: 1.25 !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-actions {
        gap: 6px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-modal-action {
        min-height: 28px !important;
        padding: 6px 8px !important;
        font-size: 10px !important;
      }

      .dwell-shoppable-video-section .dwell-sv-more-info {
        font-size: 10px !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function getGaffitReelsVariantId(form) {
  return form?.querySelector('[data-sv-variant-input]')?.value || '';
}

function ensureGaffitReelsBuyNow(form) {
  const actions = form?.querySelector('.dwell-sv-modal-actions');
  const addButton = form?.querySelector('[data-sv-submit]');
  if (!actions || !addButton || actions.querySelector('[data-sv-buy-now]')) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'dwell-sv-modal-action dwell-sv-buy-now';
  button.dataset.svBuyNow = 'true';
  button.textContent = 'Buy Now';
  button.disabled = addButton.disabled;
  actions.insertBefore(button, addButton.nextSibling);
}

function refreshGaffitReelsFallback() {
  const sections = document.querySelectorAll('.dwell-shoppable-video-section');
  if (!sections.length) return;

  let hasReelsModal = false;
  sections.forEach((section) => {
    if (!section.querySelector('.dwell-sv-modal-shell')) return;
    hasReelsModal = true;
    section.dataset.reelsFallback = 'gaffit-compact-2026-06-07';
    section.querySelectorAll('[data-sv-form]').forEach(ensureGaffitReelsBuyNow);
  });

  if (hasReelsModal) {
    ensureGaffitReelsFallbackStyle();
  }
}

onDocumentLoaded(() => {
  refreshGaffitReelsFallback();

  document.addEventListener('click', (event) => {
    const buyNowButton = event.target.closest?.('[data-sv-buy-now]');
    if (!buyNowButton || buyNowButton.disabled) return;

    const form = buyNowButton.closest('[data-sv-form]');
    const variantId = getGaffitReelsVariantId(form);
    if (!variantId) return;

    window.location.href = `/cart/${encodeURIComponent(variantId)}:1?checkout`;
  });

  const observer = new MutationObserver(refreshGaffitReelsFallback);
  observer.observe(document.body, { childList: true, subtree: true });
});
